import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { videos } from '@/mocks/home';
import { fetchVideoInfo, fetchVideoList } from '@/services/video';
import { adaptVideo, adaptVideos } from '@/services/adapters';
import type { VideoItem } from '@/types/content';
import { useRequest } from '@/hooks/useRequest';

export default function VideoDetailPage() {
  const { id } = useParams();
  const videoId = Number(id);
  const [playing, setPlaying] = useState(false);
  const [notice, setNotice] = useState(false);

  // 切换视频时重置播放状态
  useEffect(() => {
    setPlaying(false);
    setNotice(false);
  }, [videoId]);

  // 视频详情（接口失败时降级为 mock 数据）
  const { data: remoteVideo } = useRequest(
    () => fetchVideoInfo(videoId).then((res) => adaptVideo(res.dataInfo)),
    { fallback: null as VideoItem | null },
  );

  // 相关视频（接口失败时降级为 mock 数据）
  const { data: relatedList } = useRequest(
    () => fetchVideoList({ pageIndex: 1, pageSize: 5 }).then((res) => adaptVideos(res.list)),
    { fallback: videos.slice(0, 5) },
  );

  const mockVideo = videos.find((item) => item.id === videoId) ?? null;
  const video: VideoItem | null = remoteVideo ?? mockVideo;

  if (!video) {
    return (
      <PageShell>
        <PageHero
          eyebrow="视频中心"
          title="视频不存在"
          description="抱歉，未找到该视频，可能链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '视频中心', to: '/videos' }, { label: '视频详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/videos"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回视频中心
          </Link>
        </section>
      </PageShell>
    );
  }

  const related = relatedList.filter((item) => item.id !== videoId);

  return (
    <PageShell>
      <PageHero
        eyebrow="视频中心"
        title={video.title}
        breadcrumb={[{ label: '首页', to: '/' }, { label: '视频中心', to: '/videos' }, { label: '视频详情' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-background-200">
              {playing && video.videoUrl ? (
                <video
                  src={video.videoUrl}
                  poster={video.image}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain bg-foreground-950"
                >
                  您的浏览器暂不支持视频播放，请更换浏览器后重试。
                </video>
              ) : (
                <>
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - 视频专区`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-foreground-950/35"></div>
                  <button
                    type="button"
                    onClick={() => (video.videoUrl ? setPlaying(true) : setNotice(true))}
                    aria-label="播放视频"
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 flex items-center justify-center rounded-full bg-background-50/90 text-primary-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-3xl"></i>
                  </button>
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-accent-700">
                    {video.category}
                  </span>
                  {video.duration && (
                    <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded bg-foreground-950/70 text-background-50 text-xs">
                      {video.duration}
                    </span>
                  )}
                </>
              )}
            </div>

            {notice && (
              <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-md bg-secondary-50 border border-secondary-100 text-sm text-secondary-900">
                <i className="ri-information-line"></i>
                该视频暂未上传视频文件，敬请期待。
              </div>
            )}

            <h2 className="mt-6 font-heading text-xl md:text-2xl font-bold text-foreground-900 leading-snug">
              {video.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {video.views} 次播放
              </span>
              {video.duration && (
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
              )}
            </div>

            <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              视频简介
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              本视频以纪录片视角，记录中医药传承与中西医结合实践中的探索历程。
              通过真实的诊室故事与临床案例，展现名医辨证思路的沉淀与传承，帮助观众更直观地理解中西医协同的诊疗价值。
            </p>
            <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
              内容适合中医药从业者、医学院校师生及关注中医药文化的观众观看。
            </p>
          </div>

          <aside className="lg:col-span-1">
            <h3 className="font-heading font-semibold text-foreground-900 mb-4">相关视频</h3>
            <div className="flex flex-col gap-4">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/videos/${item.id}`}
                  className="group flex gap-3 p-3 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="relative w-28 h-[68px] rounded-md overflow-hidden shrink-0">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={`${item.title} - 视频专区`}
                      className="w-full h-full object-cover object-top"
                    />
                    {item.duration && (
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-foreground-950/70 text-background-50 text-[10px]">
                        {item.duration}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-heading font-medium text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h4>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-eye-line"></i>
                      {item.views}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}